import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Download, Share, PlusSquare, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getInstallEvent, subscribeInstall, promptInstall, isIOS } from "@/lib/install";
import { isStandalone } from "@/lib/pwa";
import { EASE } from "@/lib/motion";

const SNOOZE_DAYS = 14;
const HIDDEN = ["/auth", "/reset-password", "/ficha", "/admin-setup"];

const scopeOf = (p: string) => (p.startsWith("/admin") ? "admin" : p.startsWith("/pdv") ? "pdv" : "client");
const COPY = {
  client: { title: "Instalar a app Mente Sã", text: "Reserve em poucos toques, acompanhe as suas marcações e receba lembretes diretamente no telemóvel." },
  admin: { title: "Instalar o painel Mente Sã", text: "Aceda à gestão, à agenda e às métricas como uma app, com avisos de novas reservas." },
  pdv: { title: "Instalar o Caixa (PDV) Mente Sã", text: "Abra o caixa e a agenda diretamente do ecrã inicial, em ecrã inteiro." },
} as const;

const read = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };

/** Asks the visitor (client site, admin or PDV) whether they want to install the app. */
export const InstallPrompt = () => {
  const { pathname } = useLocation();
  const scope = scopeOf(pathname);
  const [open, setOpen] = useState(false);
  const [canPrompt, setCanPrompt] = useState(!!getInstallEvent());
  const [iosHelp, setIosHelp] = useState(false);
  const ios = isIOS();

  useEffect(() => subscribeInstall(() => setCanPrompt(!!getInstallEvent())), []);

  useEffect(() => {
    setOpen(false);
    if (isStandalone() || HIDDEN.some((h) => pathname.startsWith(h))) return;
    if (read(`ms_install_never_${scope}`) === "1") return;
    const until = Number(read(`ms_install_snooze_${scope}`) || 0);
    if (until > Date.now()) return;
    if (!canPrompt && !ios) return; // browser can't install (or already installed)
    const t = setTimeout(() => setOpen(true), 3500);
    return () => clearTimeout(t);
  }, [scope, pathname, canPrompt, ios]);

  const later = () => { write(`ms_install_snooze_${scope}`, String(Date.now() + SNOOZE_DAYS * 86400000)); setOpen(false); };
  const never = () => { write(`ms_install_never_${scope}`, "1"); setOpen(false); };
  const accept = async () => {
    if (canPrompt) { const ok = await promptInstall(); if (ok) setOpen(false); else later(); }
    else setIosHelp(true);
  };

  const c = COPY[scope];
  return (
    <AnimatePresence>
      {open && (
        <motion.div role="dialog" aria-label={c.title}
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } }} exit={{ opacity: 0, y: 40, transition: { duration: 0.25 } }}
          className="fixed inset-x-3 bottom-3 z-[70] mx-auto max-w-md rounded-2xl border border-border bg-card p-4 shadow-2xl pb-[calc(1rem+env(safe-area-inset-bottom))] sm:bottom-6">
          <button type="button" aria-label="Fechar" onClick={later} className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"><X size={18} /></button>
          <div className="flex gap-3">
            <img src="/icons/icon-192.png" alt="" className="h-12 w-12 rounded-xl shrink-0" />
            <div className="pr-5">
              <p className="font-display font-semibold leading-tight">{c.title}</p>
              <p className="mt-1 text-sm text-muted-foreground">{c.text}</p>
            </div>
          </div>
          {iosHelp ? (
            <ol className="mt-3 space-y-1.5 text-sm">
              <li className="flex items-center gap-2"><Share size={16} className="text-primary" /> Toque em <b>Partilhar</b> no Safari</li>
              <li className="flex items-center gap-2"><PlusSquare size={16} className="text-primary" /> Escolha <b>Adicionar ao ecrã principal</b></li>
            </ol>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button className="rounded-full gap-2" onClick={accept}><Download size={16} /> Instalar</Button>
              <Button variant="ghost" className="rounded-full" onClick={later}>Agora não</Button>
              <button type="button" onClick={never} className="ml-auto text-xs text-muted-foreground underline">Não perguntar mais</button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
